import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    HiOutlinePaperClip,
    HiOutlineDownload,
    HiOutlineDocumentText,
    HiOutlinePlay,
} from 'react-icons/hi'
import { Tooltip } from 'components/ui'
import AttachmentPreviewDialog, { isImageAttachment, isVideoAttachment, isPreviewableAttachment } from './AttachmentPreviewDialog'
import AsideCard from './AsideCard'

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return null
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const TicketAttachmentsCard = ({ ticket_attachment, embedded = false }) => {
    const { t } = useTranslation()
    const [previewFile, setPreviewFile] = useState(null)

    const attachments = Array.isArray(ticket_attachment) ? ticket_attachment : []
    const count = attachments.length

    const previewables = attachments.filter(isPreviewableAttachment)
    const files = attachments.filter((a) => !isPreviewableAttachment(a))

    // Sin adjuntos no hay card: el aside ya arrastra Detalles y Vinculadas, y una
    // tercera cuyo único contenido es "Sin adjuntos" alarga la columna para decir
    // que no hay nada. Va después de los hooks, no antes, para no cambiar su orden
    // entre un hallazgo con adjuntos y uno sin.
    if (count === 0) return null

    return (
        <AsideCard
            embedded={embedded}
            icon={<HiOutlinePaperClip className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
            title={t('attachments', 'Adjuntos')}
            headerExtra={
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-gray-100 dark:bg-gray-700 text-[10px] font-semibold leading-none text-gray-600 dark:text-gray-300 ml-1">
                    {count}
                </span>
            }
        >
            <>
                {/* Grilla de imagenes y videos */}
                {previewables.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                        {previewables.map((item) => {
                            const isVideo = isVideoAttachment(item)
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => setPreviewFile(item)}
                                    className="group relative aspect-square overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 hover:border-indigo-300 dark:hover:border-indigo-600 transition-colors"
                                    title={item.original_name || ''}
                                >
                                    {isVideo ? (
                                        <>
                                            <video
                                                src={item.file}
                                                preload="metadata"
                                                muted
                                                playsInline
                                                className="w-full h-full object-cover bg-black"
                                            />
                                            <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                                                <span className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center">
                                                    <HiOutlinePlay className="w-4 h-4 text-gray-900 translate-x-[1px]" />
                                                </span>
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <img
                                                src={item.file}
                                                alt={item.original_name || 'attachment'}
                                                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                                                loading="lazy"
                                            />
                                            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                        </>
                                    )}
                                </button>
                            )
                        })}
                    </div>
                )}

                {/* Lista compacta de archivos no-previewables */}
                {files.length > 0 && (
                    <div className={`flex flex-col gap-1.5 ${previewables.length > 0 ? 'mt-3' : ''}`}>
                        {files.map((file) => {
                            const ext = (file.extension || '').toLowerCase()
                            const size = formatBytes(file.size)
                            return (
                                <div
                                    key={file.id}
                                    className="flex items-center gap-2.5 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 px-2.5 py-2 transition-colors"
                                >
                                    <span className="w-9 h-9 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 shrink-0">
                                        <HiOutlineDocumentText className="w-4 h-4" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[12.5px] font-medium text-gray-800 dark:text-gray-100 truncate leading-tight">
                                            {file.original_name || `archivo.${ext || 'file'}`}
                                        </p>
                                        {(ext || size) && (
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate leading-tight mt-0.5">
                                                {[ext.toUpperCase(), size].filter(Boolean).join(' · ')}
                                            </p>
                                        )}
                                    </div>
                                    <Tooltip title={t('download', 'Descargar')}>
                                        <a
                                            href={file.file}
                                            download={file.original_name}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors shrink-0"
                                        >
                                            <HiOutlineDownload className="w-4 h-4" />
                                        </a>
                                    </Tooltip>
                                </div>
                            )
                        })}
                    </div>
                )}

                <AttachmentPreviewDialog
                    file={previewFile}
                    isOpen={!!previewFile}
                    onClose={() => setPreviewFile(null)}
                />
            </>
        </AsideCard>
    )
}

export default TicketAttachmentsCard
